import { useInfiniteQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { Phone, PhoneCall, PhoneOff, Users } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { callErrorText } from '../components/CallDock';
import { Badge, Button, Card, CardHeader, EmptyState, ErrorState, LoadingBlock, PageHeader, Table, Tabs, Td, Th, Tr, useToast } from '../components/ui';
import { useAuth } from '../lib/auth';
import { useCallSession } from '../lib/call-session';
import {
  OUTCOME_LABEL,
  OUTCOME_TONE,
  callClock,
  callsApi,
  listSeparator,
  useCallablePeople,
  useCallsStatus,
  type CallablePerson,
  type ClientCallSummary,
} from '../lib/calls';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { formatDateTime } from '../lib/format';
import { clientPersonName, companyName } from '../lib/people';

type Filter = 'all' | 'answered' | 'unanswered';

/**
 * Calls (operator, 2026-11-01): the client calls the people Viganium chose for it on its form in the admin —
 * each with whether they are working now — and reads back its calls: who of the client called, whom, how it
 * went. Every call is recorded, and says so before it is made.
 */
export function CallsPage() {
  const { t } = useI18n();
  const { data: status } = useCallsStatus();
  return (
    <>
      <PageHeader title={t('calls.title')} subtitle={t('calls.subtitle')} />
      {status && !status.available && (
        <Card className="mb-5 border-s-4 border-warning">
          <p className="text-body-sm text-[var(--text-primary)]">{t('calls.unavailable')}</p>
        </Card>
      )}
      <People available={!!status?.available} />
      <History />
    </>
  );
}

/** The people the client may call, each with a Call button. */
function People({ available }: { available: boolean }) {
  const { t } = useI18n();
  const { data: people, isLoading, error, refetch } = useCallablePeople();
  return (
    <Card className="mb-5">
      <CardHeader title={t('calls.whoYouCanCall')} subtitle={t('calls.whoYouCanCallHint')} />
      {isLoading ? (
        <LoadingBlock />
      ) : error ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : !people?.length ? (
        <EmptyState icon={<Users className="h-8 w-8" />} title={t('calls.nobody')} hint={t('calls.nobodyHint')} />
      ) : (
        <>
          <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {people.map((person) => (
              <PersonCard key={person.id} person={person} available={available} />
            ))}
          </ul>
          <p className="mt-4 flex items-center gap-2 text-body-sm text-error-fg dark:text-error-dark">
            <span className="h-2 w-2 shrink-0 rounded-full bg-error" aria-hidden />
            {t('calls.recordedNotice')}
          </p>
        </>
      )}
    </Card>
  );
}

function PersonCard({ person, available }: { person: CallablePerson; available: boolean }) {
  const { t, localized } = useI18n();
  const toast = useToast();
  const session = useCallSession();
  const [calling, setCalling] = useState(false);
  const name = localized(person);
  const state = person.onCall
    ? { label: t('calls.person.onCall'), tone: 'warning' as const }
    : person.onBreak
      ? { label: t('calls.person.onBreak'), tone: 'warning' as const }
      : person.working
        ? { label: t('calls.person.working'), tone: 'success' as const }
        : { label: t('calls.person.notWorking'), tone: 'neutral' as const };
  const call = async () => {
    setCalling(true);
    try {
      await session.start([person.id]);
    } catch (e) {
      toast.error(callErrorText(e, t));
    } finally {
      setCalling(false);
    }
  };
  return (
    <li className="flex items-center gap-3 rounded-md border hairline p-3">
      <span
        className={clsx(
          'grid h-10 w-10 shrink-0 place-items-center rounded-full text-caption font-semibold',
          person.working && !person.onBreak ? 'bg-success/15 text-success-fg dark:text-success' : 'bg-neutral-500/12 text-[var(--text-secondary)]',
        )}
        aria-hidden
      >
        {initials(person.fullName)}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-body-sm font-medium text-[var(--text-primary)]">{name}</p>
        {person.jobTitle && <p className="truncate text-caption text-[var(--text-secondary)]">{person.jobTitle}</p>}
        <Badge tone={state.tone} className="mt-1">
          {state.label}
        </Badge>
      </div>
      <Button
        size="sm"
        variant="primary"
        loading={calling}
        disabled={!available || !!session.current}
        onClick={() => void call()}
        aria-label={t('calls.callPerson', { name })}
        title={session.current ? t('calls.error.alreadyInCall') : t('calls.callPerson', { name })}
      >
        <Phone className="h-4 w-4" aria-hidden />
        {t('calls.call')}
      </Button>
    </li>
  );
}

/** The client's calls, the latest first. */
function History() {
  const { t, locale, localized } = useI18n();
  const { account } = useAuth();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<Filter>('all');
  const query = useInfiniteQuery({
    queryKey: ['calls', 'history', filter],
    queryFn: ({ pageParam }) => callsApi.history({ filter, cursor: pageParam ?? undefined, limit: 25 }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    refetchInterval: 30_000,
  });
  const rows = query.data?.pages.flatMap((p) => p.items) ?? [];
  const company = companyName(account, locale);
  const madeBy = (c: ClientCallSummary) => (c.madeBy ? clientPersonName(c.madeBy, company, t('calls.you')) : company);
  return (
    <Card padded={false}>
      <div className="px-5 pt-5">
        <CardHeader title={t('calls.yourCalls')} subtitle={t('calls.yourCallsHint')} />
      </div>
      <Tabs
        active={filter}
        onChange={(id) => setFilter(id as Filter)}
        tabs={(['all', 'answered', 'unanswered'] as Filter[]).map((f) => ({ id: f, label: t(`calls.filter.${f}` as TranslationKey) }))}
      />
      {query.isLoading ? (
        <LoadingBlock />
      ) : query.error ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState icon={<PhoneCall className="h-8 w-8" />} title={t('calls.noneYet')} hint={t('calls.noneYetHint')} />
      ) : (
        <>
          <Table>
            <thead>
              <tr>
                <Th>{t('calls.with')}</Th>
                <Th>{t('calls.outcome')}</Th>
                <Th>{t('calls.madeBy')}</Th>
                <Th>{t('calls.when')}</Th>
                <Th align="end">{t('calls.duration')}</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <Tr key={c.id} onClick={() => navigate(`/calls/${c.id}`)}>
                  <Td>
                    <span className="flex min-w-0 items-center gap-2">
                      {c.startedAt ? (
                        <PhoneCall className="h-4 w-4 shrink-0 text-[var(--text-secondary)]" aria-hidden />
                      ) : (
                        <PhoneOff className="h-4 w-4 shrink-0 text-[var(--text-secondary)]" aria-hidden />
                      )}
                      {c.type === 'GROUP' && <Users className="h-3.5 w-3.5 shrink-0 text-[var(--text-secondary)]" aria-label={t('calls.groupCall')} />}
                      <span className="truncate text-[var(--text-primary)]">
                        {c.people.map((p) => localized(p)).join(listSeparator(locale)) || '—'}
                      </span>
                    </span>
                  </Td>
                  <Td>
                    <Badge tone={OUTCOME_TONE[c.outcome]}>{t(OUTCOME_LABEL[c.outcome])}</Badge>
                  </Td>
                  <Td className="whitespace-nowrap text-[var(--text-secondary)]">{madeBy(c)}</Td>
                  <Td className="whitespace-nowrap text-[var(--text-secondary)]">{formatDateTime(c.createdAt, locale)}</Td>
                  <Td align="end" className="tabular">
                    {c.durationSeconds !== null ? callClock(c.durationSeconds) : '—'}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
          {query.hasNextPage && (
            <div className="flex justify-center border-t hairline p-3">
              <Button size="sm" loading={query.isFetchingNextPage} onClick={() => void query.fetchNextPage()}>
                {t('calls.loadMore')}
              </Button>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

function initials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}
